/**
 * Shared onboarding-recommendation wire schemas.
 *
 * The canonical backend endpoint is
 *   POST /api/onboarding/v1/onboarding/recommended-default
 *
 * The endpoint is PUBLIC (no session required) and TENANT-scoped
 * via host. The wire shape mirrors the Go
 * `onboarding.RecommendedDefault` struct.
 *
 * The TS-side canonical text resolution lives in
 * `@pureid/ui/lib/onboarding-slots`; this schema only validates
 * the wire shape. Slot keys are validated as plain strings
 * (rather than the strict `SlotKey` union) so the schema is
 * forward-compatible with a backend that adds new slots before
 * the frontend is rebuilt — unknown keys emit a console.warn
 * (see resolveSlot) and the chip degrades gracefully.
 *
 * The request body optionally carries a UA hint so the server
 * can refine the generic "this device" hint to OS-specific copy
 * (Mac with Touch ID, Windows 11 with Hello, etc.). The hint is
 * parsed via the same `parseUaHint()` already used by the
 * client-telemetry pipeline — single OS-detection surface
 * across the codebase.
 */

import { z } from "zod";

export interface RecommendedDefaultRequest {
  ua_hint?: {
    os: string;
    /**
     * Browser family — drives the DD-9 proactive-guidance rule.
     * Linux + Firefox short-circuits the default phone-QR path
     * because Firefox-on-Linux can't pair caBLE; we proactively
     * promote a security key to primary BEFORE the user wastes
     * a click on a QR pairing that won't complete.
     *
     * Optional for forward-compat with older backends; absent or
     * unrecognised falls through to the OS-only path. Vocabulary
     * mirrors `parseUaHint().browser` from `@pureid/auth-core`:
     * "Chrome" / "Edge" / "Firefox" / "Safari" / "Unknown".
     */
    browser?: string;
  };
}

export const recommendedAlternativeSchema = z.object({
  method: z.enum([
    "platform_passkey",
    "phone_qr",
    "security_key",
    "tap",
    "fragile_redirect",
    "none",
  ]),
  reason_slot: z.string(),
});
export type RecommendedAlternativeWire = z.infer<
  typeof recommendedAlternativeSchema
>;

export const recommendedDefaultResponseSchema = z.object({
  device_hint_slot: z.string(),
  method: z.enum([
    "platform_passkey",
    "phone_qr",
    "security_key",
    "tap",
    "fragile_redirect",
    "none",
  ]),
  reason_slot: z.string(),
  alternatives: z.array(recommendedAlternativeSchema),
  provider: z.string(),
});
export type RecommendedDefaultResponse = z.infer<
  typeof recommendedDefaultResponseSchema
>;
